"use client";
import Head from "next/head";
import { PricingSection } from "components/modules/Pricing/PricingSection";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { translateFunc } from "client/utils/translateFunc";
import { Navbar } from "@/components/modules/Navigation/Navbar/Navbar";
import { PackageDetailsTable } from "@/components/modules/Pricing/PackageDetailsTable";
import { FaqPricing } from "@/components/modules/Pricing/FaqForPricing";
import { TrustPilotSection } from "@/components/modules/TrustPilot/TrustPilotSection";
import { RouteNames } from "@/RouteNames";
import { commonLanguageFiles } from "@/config";
import { LanguageProvider } from "@/contexts/LanguageProvider";
import { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTemporaryQueryParam } from "@/hooks/utils/useTemporaryQueryParam";
import { useEffect } from "react";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";

const Page = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");
    const [maxFileSizeError] = useTemporaryQueryParam("maxFileSizeError");
    const isMaxFileSizeError = maxFileSizeError === "true";
    const { showMaxFileSizeErrorToast } = useDailyQuota();

    useEffect(() => {
        if (!isMaxFileSizeError) return;
        showMaxFileSizeErrorToast();
    }, [isMaxFileSizeError]);

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
            </Head>

            <div className="max-w-1440 page-shadow dark:bg-darkSurface-100 dark:text-gray-100">
                <Navbar url={RouteNames.pricing} />
                <PricingSection />
                <PackageDetailsTable />
                <FaqPricing />
                <TrustPilotSection />
                <Footer url={RouteNames.pricing} />
            </div>
        </>
    );
};

const files = ["pricing-page", ...commonLanguageFiles];

const LanguageWrapper = () => {
    return (
        <LanguageProvider files={files}>
            <Page />
        </LanguageProvider>
    );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
